import React from "react";
import { Alert, Button, Spin, Tooltip } from "antd";
import moment from "moment";
import {
  AiOutlineAppstore,
  AiOutlineDollarCircle,
  AiOutlineFileText,
  AiOutlineReload,
} from "react-icons/ai";
import { FaBusAlt, FaChalkboardTeacher, FaUserGraduate } from "react-icons/fa";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/auth.slice";
import { fetchGroupDashboard360 } from "../../services/groupConsole.service";
import StickySectionNav from "../admin/pages/components/dashboard360/StickySectionNav";
import useScrollSpy from "../admin/pages/components/dashboard360/useScrollSpy";
import { SECTION_COLORS } from "../admin/pages/components/dashboard360/chartTheme";
import OverviewSection from "../admin/pages/components/dashboard360/sections/OverviewSection";
import FinanceSection from "../admin/pages/components/dashboard360/sections/FinanceSection";
import StudentsSection from "../admin/pages/components/dashboard360/sections/StudentsSection";
import AdmissionsSection from "../admin/pages/components/dashboard360/sections/AdmissionsSection";
import StaffSection from "../admin/pages/components/dashboard360/sections/StaffSection";
import OperationsSection from "../admin/pages/components/dashboard360/sections/OperationsSection";

/**
 * The group's Is-Kool-360° — same 6 sections, same charts, same components
 * as the branch admin's own Iskool360Dashboard.js (imported directly, not
 * copied), fed by GroupConsoleService.getDashboard360's combined-across-
 * branches data instead of one school's. Deep-link badges/rows (e.g.
 * "5 pending payment approvals") go to a no-op here rather than a specific
 * branch's admin screen — there's no single branch to act on when the
 * numbers on screen are a group total.
 */
const SECTIONS = [
  { id: "overview", label: "Overview", icon: <AiOutlineAppstore size={15} />, color: SECTION_COLORS.overview },
  { id: "finance", label: "Finance & Fees", icon: <AiOutlineDollarCircle size={15} />, color: SECTION_COLORS.finance },
  { id: "students", label: "Students & Attendance", icon: <FaUserGraduate size={14} />, color: SECTION_COLORS.students },
  { id: "admissions", label: "Admissions", icon: <AiOutlineFileText size={15} />, color: SECTION_COLORS.admissions },
  { id: "staff", label: "Staff", icon: <FaChalkboardTeacher size={14} />, color: SECTION_COLORS.staff },
  { id: "operations", label: "Operations", icon: <FaBusAlt size={14} />, color: SECTION_COLORS.operations },
];
const SECTION_IDS = SECTIONS.map((s) => s.id);

const noop = () => {};

const getCurrentAcademicYear = () => {
  const today = new Date();
  const curYear = today.getFullYear();
  const curMonth = today.getMonth() + 1;
  const startYear = curMonth >= 6 ? curYear : curYear - 1;
  const endYearShort = String(startYear + 1).slice(-2);
  return `${startYear}-${endYearShort}`;
};

const EMPTY = {
  overview: {
    totalStudents: 0, totalTeachers: 0, totalParents: 0, totalClasses: 0, totalSubjects: 0,
    pendingApplications: 0, applicationsThisMonth: 0, pendingTransactions: 0, pendingDiscounts: 0,
    pendingCertificateRequests: 0, transport: { buses: 0, activeRoutes: 0, transportStudents: 0 },
    announcementsThisMonth: 0,
  },
  attendance: { trend: [], today: {} },
  fee: {
    expected: 0, collected: 0, remaining: 0, discount: 0, studentFees: 0, studentsWithDues: 0,
    collectionRate: 0, statusDistribution: {}, monthlyCollections: [],
  },
  distribution: { byClass: [], byGender: [] },
  admissions: { byStatus: {}, monthlyTrend: [] },
  activity: { payments: [], announcements: [] },
  staffOps: {
    totalInstructors: 0,
    attendanceToday: { onTime: 0, late: 0, onLeaveOrPermission: 0, notCheckedIn: 0, total: 0 },
    attendanceTrend: [],
    leaveRequests: { pending: 0, approvedThisMonth: 0, rejectedThisMonth: 0, pendingByType: { permission: 0, leave: 0, sick: 0 } },
    departmentDistribution: [],
  },
};

const GroupHome = () => {
  const user = useSelector(selectUser);
  const groupId = user?.group;
  const queryClient = useQueryClient();
  const academicYear = getCurrentAcademicYear();

  const dashboard = useQuery(
    ["group-console-dashboard360", groupId, academicYear],
    () => fetchGroupDashboard360(groupId, academicYear),
    { enabled: !!groupId },
  );
  const data = dashboard.data || EMPTY;

  const { activeId, scrollToSection } = useScrollSpy(SECTION_IDS, {
    ready: !dashboard.isLoading,
  });

  return (
    <div className="bg-slate-50 min-h-screen p-8">
      <div
        className="rounded-2xl p-6 md:p-8 mb-4 text-white shadow-md"
        style={{ background: "linear-gradient(120deg, #03212B 0%, #09617E 55%, #0C86AE 100%)" }}
      >
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-white">
              Is-Kool-360&deg; — All Branches
            </h1>
            <p className="text-sky-100 mt-1">
              Combined across {data.branchCount ?? 0} branch
              {data.branchCount === 1 ? "" : "es"}. Click a branch name in any
              Group Console list to switch in and manage things there.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-right">
              <div className="text-sm font-semibold text-white">
                {moment().format("dddd, DD MMMM YYYY")}
              </div>
              <div className="text-xs text-sky-100">Academic Year {academicYear}</div>
            </div>
            <Tooltip title="Refresh metrics">
              <Button
                shape="circle"
                icon={<AiOutlineReload className={dashboard.isFetching ? "animate-spin" : ""} />}
                onClick={() => queryClient.invalidateQueries(["group-console-dashboard360", groupId])}
                className="flex items-center justify-center"
              />
            </Tooltip>
          </div>
        </div>
      </div>

      {!!data.errors?.length && (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message={`Could not load ${data.errors.length} branch${data.errors.length > 1 ? "es" : ""}: ${data.errors
            .map((e) => e.school_name)
            .join(", ")}`}
        />
      )}

      <StickySectionNav sections={SECTIONS} activeId={activeId} onSelect={scrollToSection} />

      {dashboard.isLoading ? (
        <div className="flex justify-center items-center h-96">
          <Spin size="large" tip="Assembling your group's 360° view..." />
        </div>
      ) : (
        <div className={`transition-opacity duration-300 ${dashboard.isFetching ? "opacity-60 pointer-events-none" : ""}`}>
          <OverviewSection
            overview={data.overview}
            attendance={data.attendance}
            fee={data.fee}
            staffOps={data.staffOps}
            activity={data.activity}
            distribution={data.distribution}
            onNavigate={noop}
            onJumpToSection={scrollToSection}
          />
          <FinanceSection
            fee={data.fee}
            overview={data.overview}
            academicYear={academicYear}
            onNavigate={noop}
          />
          <StudentsSection
            overview={data.overview}
            attendance={data.attendance}
            distribution={data.distribution}
          />
          <AdmissionsSection overview={data.overview} admissions={data.admissions} />
          <StaffSection overview={data.overview} staffOps={data.staffOps} onNavigate={noop} />
          <OperationsSection overview={data.overview} onNavigate={noop} />
        </div>
      )}
    </div>
  );
};

export default GroupHome;
